import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  RefreshRounded,
  RocketLaunchRounded,
  PeopleRounded,
  CheckCircleRounded,
  ErrorRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import { api } from "../utils/api";
import { PCC_AUTH_URL } from "../constants/pcc";
import dayjs from "dayjs";

const SESSION_KEY = "pcc_patient_search";
const TOKEN_SESSION_KEY = "pcc_access_token";
const TOKEN_EXPIRY_KEY = "pcc_token_expiry";
const RESULTS_SESSION_KEY = "pcc_search_results";

const SmartCallback: React.FC = () => {
  const navigate = useNavigate();
  const hasRun = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [fetchedPatientId, setFetchedPatientId] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [viewingPatientId, setViewingPatientId] = useState<string | null>(null);

  const getPatientName = (p: any) => {
    const name = p.name?.[0];
    if (!name) return "N/A";
    return `${name?.given?.join(" ") || ""} ${name?.family || ""}`.trim();
  };

  const getPhone = (p: any) => {
    const phones = (p.telecom || []).filter((t: any) => t.system === "phone");
    if (!phones.length) return "N/A";
    return phones
      .slice(0, 2)
      .map((t: any) => {
        const label = t.use ? t.use.charAt(0).toUpperCase() + t.use.slice(1) + ": " : "";
        return `${label}${t.value}`;
      })
      .join(" · ");
  };

  const getCity = (p: any) => {
    const addr = (p.address || [])[0];
    return addr?.city || "N/A";
  };

  const handleViewPatient = (patientId: string) => {
    setViewingPatientId(patientId);
    const token = sessionStorage.getItem(TOKEN_SESSION_KEY);
    if (token) {
      api
        .get(`/pcc/patient-details/${patientId}`, { params: { token } })
        .then(() => navigate(`/patients/${patientId}`))
        .catch(() => {
          // Token expired — fall back to OAuth
          sessionStorage.removeItem(TOKEN_SESSION_KEY);
          sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
          window.location.href = PCC_AUTH_URL.replace("state=abc123", `state=fetch-details:${patientId}`);
        });
    } else {
      window.location.href = PCC_AUTH_URL.replace("state=abc123", `state=fetch-details:${patientId}`);
    }
  };

  const exchangeToken = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state") ?? "";

    if (!code) {
      setError("Authorization code not found in callback URL.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      if (state === "search-patient") {
        // On back-navigation the code is stale but results are cached — restore them
        const cachedResults = sessionStorage.getItem(RESULTS_SESSION_KEY);
        if (cachedResults) {
          setSearchResults(JSON.parse(cachedResults));
        } else {
          const raw = sessionStorage.getItem(SESSION_KEY);
          const searchParams: Record<string, string> = raw ? JSON.parse(raw) : {};
          sessionStorage.removeItem(SESSION_KEY);

          const res = await api.get("/pcc/patients-search", {
            params: { code, ...searchParams },
          });

          if (res.data.accessToken) {
            sessionStorage.setItem(TOKEN_SESSION_KEY, res.data.accessToken);
            sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + 55 * 60 * 1000));
          }

          const patients = res.data.patients ?? [];
          sessionStorage.setItem(RESULTS_SESSION_KEY, JSON.stringify(patients));
          setSearchResults(patients);
        }
      } else if (state.startsWith("fetch-details:")) {
        const patientId = state.replace("fetch-details:", "");
        await api.get(`/pcc/patient-details/${patientId}`, { params: { code } });
        setFetchedPatientId(patientId);
      } else {
        await api.get("/pcc/patients-only", { params: { code } });
      }

      setSuccess(true);
    } catch (err: any) {
      console.error("SMART callback error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Unexpected error during data fetch",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    void exchangeToken();
  }, [exchangeToken]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        bgcolor: "background.default",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, md: 6 },
          width: "100%",
          maxWidth: searchResults && searchResults.length > 0 ? "100%" : 600,
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 10px 50px rgba(0,0,0,0.04)",
          textAlign: "center",
          transition: "max-width 0.3s ease",
        }}
      >
        {loading ? (
          <Stack spacing={3} alignItems="center">
            <CircularProgress size={60} thickness={4} />
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Fetching PCC Data
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we sync your patient records from PointClickCare.
              </Typography>
            </Box>
          </Stack>
        ) : error ? (
          <Stack spacing={4}>
            <Box>
              <ErrorRounded color="error" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Fetch Failed
              </Typography>
              <Alert severity="error" sx={{ borderRadius: 1, textAlign: "left" }}>
                {error}
              </Alert>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<RefreshRounded />}
                onClick={() => void exchangeToken()}
                sx={{ borderRadius: "4px", fontWeight: 700 }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                startIcon={<RocketLaunchRounded />}
                onClick={() => (window.location.href = PCC_AUTH_URL)}
                sx={{ borderRadius: "4px", fontWeight: 700 }}
              >
                Fetch Again
              </Button>
            </Stack>
            <Button
              variant="text"
              startIcon={<PeopleRounded />}
              onClick={() => navigate("/patients")}
              sx={{ fontWeight: 600 }}
            >
              Go to Patient Page
            </Button>
          </Stack>
        ) : success ? (
          <Stack spacing={4}>
            {searchResults !== null ? (
              <>
                <Box textAlign="left">
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Search Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchResults?.length} patient{searchResults.length !== 1 ? "s" : ""} found
                  </Typography>
                </Box>

                {searchResults.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 1, textAlign: "left" }}>
                    No patients found matching your search.
                  </Alert>
                ) : (
                  <Box>
                    {/* Table header */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2.5fr 1.5fr 0.7fr 1fr 1fr 1fr",
                        px: 2,
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        textAlign: "left",
                      }}
                    >
                      {["Patient Name", "Contact", "Gender", "Date of Birth", "City", "Actions"].map((h) => (
                        <Typography key={h} variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "11px" }}>
                          {h}
                        </Typography>
                      ))}
                    </Box>

                    {/* Rows */}
                    <Stack spacing={1} mt={1}>
                      {searchResults.map((p: any) => (
                        <Box
                          key={p.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "2.5fr 1.5fr 0.7fr 1fr 1fr 1fr",
                            alignItems: "center",
                            px: 2,
                            py: 1.5,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            textAlign: "left",
                          }}
                        >
                          {/* Name + ID */}
                          <Box>
                            <Typography variant="body2" fontWeight={700}>
                              {getPatientName(p)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: "12px" }}>
                              <Box component="span" fontWeight={700}>Id: </Box>
                              {p.id}
                            </Typography>
                          </Box>

                          {/* Contact */}
                          <Typography variant="caption" fontWeight={500}>
                            {getPhone(p)}
                          </Typography>

                          {/* Gender */}
                          <Typography variant="body2" fontWeight={500} sx={{ textTransform: "capitalize" }}>
                            {p.gender || "N/A"}
                          </Typography>

                          {/* DOB */}
                          <Typography variant="body2" fontWeight={500}>
                            {p.birthDate ? dayjs(p.birthDate).format("DD MMM YYYY") : "N/A"}
                          </Typography>

                          {/* City */}
                          <Typography variant="body2" fontWeight={500}>
                            {getCity(p)}
                          </Typography>

                          {/* Action */}
                          <Box>
                            <Tooltip
                              title="On click, it will save to the local records"
                              arrow
                              placement="top"
                            >
                              <span>
                                <Button
                                  size="small"
        
                                  variant="outlined"
                                  disabled={viewingPatientId !== null}
                                  startIcon={
                                    viewingPatientId === p.id
                                      ? <CircularProgress size={12} color="inherit" />
                                      : <VisibilityRounded sx={{ fontSize: "14px !important" }} />
                                  }
                                  onClick={() => handleViewPatient(p.id)}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    borderRadius: 1,
                                    borderColor: "divider",
                                    px: "8px",
                                    py: 0.5,
                                  }}
                                >
                                  {viewingPatientId === p.id ? "Loading..." : "View Report"}
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-start">
                  <Button
                    variant="outlined"
                    startIcon={<PeopleRounded />}
                    onClick={() => {
                      sessionStorage.removeItem(RESULTS_SESSION_KEY);
                      navigate("/patients");
                    }}
                    sx={{ borderRadius: "4px", fontWeight: 700, textTransform: "none" }}
                  >
                    Go to Patients
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Box>
                  <CheckCircleRounded color="success" sx={{ fontSize: 64, mb: 2 }} />
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Success!
                  </Typography>
                  <Alert severity="success" sx={{ borderRadius: 1, textAlign: "left" }}>
                    Patient data has been successfully fetched and synchronized.
                  </Alert>
                </Box>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    startIcon={<PeopleRounded />}
                    onClick={() => navigate(fetchedPatientId ? `/patients/${fetchedPatientId}` : "/patients")}
                    sx={{ borderRadius: "4px", fontWeight: 700 }}
                  >
                    {fetchedPatientId ? "View Patient" : "Go to Patients"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RocketLaunchRounded />}
                    onClick={() => (window.location.href = PCC_AUTH_URL)}
                    sx={{ borderRadius: "4px", fontWeight: 700 }}
                  >
                    Fetch Again
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        ) : null}
      </Paper>
    </Box>
  );
};

export default SmartCallback;
